// Which days a tracking screen looks at: a few presets and two dates, and a
// line under them that says what a day is here. The days are UTC days — the
// warmup's budget and rule 4 count on that boundary — and the line names the
// wall clock of every country in view, so "tegnap" is never a guess.

import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PRESETS, boundaryText, dayBoundary, presetRange } from "@/components/emc/days";
import type { DayRange, Preset } from "@/components/emc/days";
import { cn } from "@/lib/utils";

interface Props {
  value: DayRange;
  /** Today, as a UTC day. */
  today: string;
  onChange: (next: DayRange) => void;
  /** The countries in view, with their clocks. */
  countries?: { country: string; timezone: string }[];
  className?: string;
}

export function DayRangePicker({ value, today, onChange, countries = [], className }: Props) {
  const single = value.from === value.to;
  return (
    <div data-slot="day-range" className={cn("flex min-w-0 flex-col gap-2", className)}>
      <div className="flex flex-wrap items-center gap-3">
        <Tabs
          value={value.preset}
          onValueChange={(preset) =>
            onChange(presetRange(preset as Preset, today, { from: value.from, to: value.to }))
          }
        >
          <TabsList variant="segmented" aria-label="Időszak">
            {PRESETS.map((preset) => (
              <TabsTrigger key={preset.value} value={preset.value}>
                {preset.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {value.preset === "custom" && (
          <div className="flex items-center gap-2 [&_[data-slot=input]]:h-control-sm">
            <Input
              aria-label="Első nap"
              className="w-auto"
              type="date"
              max={today}
              value={value.from}
              onChange={(event) =>
                event.target.value &&
                onChange(presetRange("custom", today, { from: event.target.value, to: value.to }))
              }
            />
            <span className="text-muted-foreground">–</span>
            <Input
              aria-label="Utolsó nap"
              className="w-auto"
              type="date"
              max={today}
              value={value.to}
              onChange={(event) =>
                event.target.value &&
                onChange(presetRange("custom", today, { from: value.from, to: event.target.value }))
              }
            />
          </div>
        )}
      </div>
      <p className="text-label text-muted-foreground">
        <span className="data-mono text-ink">
          {single ? value.from : `${value.from} – ${value.to}`}
        </span>
        . {boundaryText(dayBoundary(countries, value.to), value.to)}
      </p>
    </div>
  );
}
